/* ===========================================================
   sync/versions.js — saving and rebuilding versions

   A saved version stores only what changed since the version before
   it (a Yjs delta against the previous version's state vector); every
   20th stores the whole doc as a checkpoint. Rebuilding a version =
   take its nearest checkpoint and apply the deltas after it, in order.

   The room only accepts a version that builds on the newest one (or a
   checkpoint when one is due), so two people saving at once can't fork
   the chain: the second gets "stale" and simply retries.
   =========================================================== */

import * as Y from 'yjs';
import { api } from '../lib/api.js';
import { writeVar, concat, unpackBundle, newId } from '../../shared/protocol.js';

const b64 = u8 => { let s = ''; for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return btoa(s); };
const unb64 = s => Uint8Array.from(atob(s), c => c.charCodeAt(0));

export function listVersions(docId, call = api) {
  return call(`/api/docs/${docId}/versions`);
}

/** Save the doc as it is now. kind: named | auto | restore | copy | merge | import */
export async function saveVersion(docId, ydoc, { kind = 'named', name = '' } = {}, call = api) {
  let { next } = await listVersions(docId, call);
  for (let attempt = 0; attempt < 4; attempt++) {
    const sv = next.sv ? unb64(next.sv) : null;
    const delta = next.checkpoint || !sv ? Y.encodeStateAsUpdate(ydoc) : Y.encodeStateAsUpdate(ydoc, sv);
    const meta = {
      id: newId(20), name, kind, prev: next.prev, checkpoint: next.checkpoint,
      sv: b64(Y.encodeStateVector(ydoc)),
    };
    const head = new TextEncoder().encode(JSON.stringify(meta));
    const len = [];
    writeVar(len, head.length);
    try {
      return await call(`/api/docs/${docId}/versions`, { method: 'POST', raw: concat([new Uint8Array(len), head, delta]) });
    } catch (err) {
      if (err.status !== 409 || !err.data?.next) throw err;
      next = err.data.next;
    }
  }
  throw new Error('Someone else kept saving versions at the same moment. Try again.');
}

/** Rebuild one saved version as its own Yjs doc. */
export async function loadVersion(docId, versionId, call = api) {
  const bytes = await call(`/api/docs/${docId}/versions/${encodeURIComponent(versionId)}`);
  const ydoc = new Y.Doc();
  for (const e of unpackBundle(bytes)) Y.applyUpdate(ydoc, e.data);
  return ydoc;
}
